import { AxiosError } from "axios";
import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";

// ─── Program Structure V2 (Main) ──────────────────────────────────────────────────

export async function createProgramStructureV2(payload: Record<string, any>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.CREATE,
      payload
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateProgramStructureV2(id: number, payload: Record<string, any>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramStructureV2ByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.GET_BY_COMPONENT_ID(componentId)
    );
    return response?.data;
  } catch (error) {
    handleApiError(error as AxiosError);
    return null;
  }
}

export async function getProgramStructureV2DataByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.GET_DATA_BY_COMPONENT_ID(componentId)
    );
    return response?.data;
  } catch (error) {
    handleApiError(error as AxiosError);
    return null;
  }
}

export async function deleteProgramStructureV2(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.DELETE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function cloneProgramStructureV2WithNestedData(
  sourceProgramStructureV2Id: number,
  targetPageComponentId: number,
  targetProgramStructureV2Id?: number | null
) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.CLONE,
      {
        source_program_structure_v2_id: sourceProgramStructureV2Id,
        target_page_component_id: targetPageComponentId,
        target_program_structure_v2_id: targetProgramStructureV2Id || null,
      }
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// ─── Semester ────────────────────────────────────────────────────────────────

export async function createProgramStructureV2Semester(payload: Record<string, any>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.SEMESTER.CREATE,
      payload
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateProgramStructureV2Semester(id: number, payload: Record<string, any>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.SEMESTER.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteProgramStructureV2Semester(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.SEMESTER.DELETE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramStructureV2SemestersByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.SEMESTER.GET_BY_COMPONENT_ID(componentId)
    );
    return response?.data;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// ─── Category ────────────────────────────────────────────────────────────────
// Categories belong to semesters (can vary per semester)

export async function createProgramStructureV2Category(payload: Record<string, any>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.CATEGORY.CREATE,
      payload
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateProgramStructureV2Category(id: number, payload: Record<string, any>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.CATEGORY.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteProgramStructureV2Category(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.CATEGORY.DELETE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramStructureV2CategoriesBySemesterId(semesterId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.CATEGORY.GET_BY_SEMESTER_ID(semesterId)
    );
    return response?.data;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// ─── Category Item ───────────────────────────────────────────────────────────

export async function createProgramStructureV2CategoryItem(payload: Record<string, any>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.CATEGORY_ITEM.CREATE,
      payload
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateProgramStructureV2CategoryItem(id: number, payload: Record<string, any>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.CATEGORY_ITEM.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteProgramStructureV2CategoryItem(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.CATEGORY_ITEM.DELETE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramStructureV2CategoryItemsByCategoryId(categoryId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.CATEGORY_ITEM.GET_BY_CATEGORY_ID(categoryId)
    );
    return response?.data;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// ─── Subject ─────────────────────────────────────────────────────────────────

export async function createProgramStructureV2Subject(payload: Record<string, any>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.SUBJECT.CREATE,
      payload
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateProgramStructureV2Subject(id: number, payload: Record<string, any>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.SUBJECT.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteProgramStructureV2Subject(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.SUBJECT.DELETE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramStructureV2SubjectsBySemesterId(semesterId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE_V2.SUBJECT.GET_BY_SEMESTER_ID(semesterId)
    );
    return response?.data;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}
